import styled, { css } from 'styled-components';
import { UiColors } from 'assets/styles/old/global';

export const Root = styled.div`
  position: relative;
  display: inline-flex;
`;

export const IconTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: ${({ active }) => (active ? UiColors.forestColor : UiColors.silverFillColor)};

  &:hover {
    background: ${UiColors.headerTableGray};
    color: ${UiColors.forestColor};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const GlobalTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid ${UiColors.strokeColor};
  border-radius: 8px;
  background: ${UiColors.fillWhiteColor};
  color: ${UiColors.forestColor};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${UiColors.cloudColor};
  }

  svg {
    width: 14px;
    height: 14px;
  }

  .label {
    color: ${UiColors.dimGreyFillColor};
    font-weight: 500;
  }

  .sort-text {
    white-space: nowrap;
  }

  /* Compact: collapse to just the direction arrow + caret, and shrink the control
     so it cannot crowd/overlap the toggle or retailer count on narrow toolbars. */
  @media (max-width: 1023px) {
    height: 32px;
    padding: 0 6px;
    gap: 2px;

    .sort-text {
      display: none;
    }

    svg {
      width: 13px;
      height: 13px;
    }
  }
`;

/* Portaled to <body> (see useAnchoredPanel) so no ancestor's `overflow` can clip it and it can't run
   off the right edge on the last column; top/left come from that hook as viewport coordinates.
   Above the modal frame (ui-kit/Modal's OverallModalWrapper, 9000) and the driving-direction
   drawers (9001), below the portaled tag/nav popovers (10000) and the app loader (9999). It sat at
   900 before, which is under all of those: inside a modal -- Add existing retailer, say -- the
   trigger opened the menu behind the modal, so the control looked dead while the per-header sort
   arrows, which are plain in-flow buttons, kept working. */
export const Menu = styled.div`
  position: fixed;
  z-index: 9500;
  min-width: 200px;
  /* Never wider than the viewport on the gutter useAnchoredPanel clamps to (16px × 2). */
  max-width: calc(100vw - 32px);
  /* The global menu lists every sortable value in the table, so cap it and scroll rather than
     running off the bottom of the screen. */
  max-height: min(60vh, 420px);
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 6px;
  background: ${UiColors.fillWhiteColor};
  border: 1px solid ${UiColors.strokeColor};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(13, 13, 13, 0.12);
`;

/* Column name heading above the fields that live under that column. */
export const MenuGroupLabel = styled.div`
  padding: 8px 6px 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${UiColors.dimGreyFillColor};

  &:not(:first-child) {
    margin-top: 2px;
    border-top: 1px solid ${UiColors.strokeColor};
  }
`;

export const MenuRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  border-radius: 6px;

  &:hover {
    background: ${UiColors.cloudColor};
  }
`;

export const MenuLabel = styled.span`
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: ${UiColors.forestColor};
  white-space: nowrap;
`;

export const DirButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid ${UiColors.strokeColor};
  border-radius: 6px;
  cursor: pointer;
  background: ${({ active }) => (active ? UiColors.mangoColor : UiColors.fillWhiteColor)};
  color: ${UiColors.forestColor};

  &:hover {
    background: ${({ active }) => (active ? UiColors.mangoColor : UiColors.cloudColor)};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

/* Clickable column header that toggles sort; sits inside the uppercase <Th>.
   Shared by every new-design list table (see ./SortableHeader). */
export const SortHeader = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: ${({ active }) => (active ? UiColors.forestColor : 'inherit')};

  /* Let a long label truncate rather than push the sort arrow out of the cell / over the next column. */
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* The only affordance when the arrow is suppressed (showArrow={false}), so the label still
     reads as clickable next to a sort menu. */
  &:hover span {
    color: ${UiColors.forestColor};
    text-decoration: underline;
  }

  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    ${({ active }) =>
      !active &&
      css`
        opacity: 0.35;
      `}
  }
`;
